import React, { useState } from 'react';
import { Globe, Link as LinkIcon, Edit2, Check, X } from 'lucide-react';
import { toast } from 'react-toastify';

const WebsiteManager = ({ websiteConfig, onSave, isProcessing }) => {
    const [isEditing, setIsEditing] = useState(!websiteConfig?.domain);
    const [domain, setDomain] = useState(websiteConfig?.domain || '');

    const handleSave = () => {
        if (!domain.trim()) {
            toast.error("Please enter a valid domain (e.g., example.com)");
            return;
        }

        // Basic URL wrapper if they didn't include protocol, to validate
        let formattedDomain = domain.trim();
        if (!/^https?:\/\//i.test(formattedDomain)) {
            formattedDomain = 'https://' + formattedDomain;
        }

        try {
            const url = new URL(formattedDomain);
            // We just want to store the hostname (e.g. example.com)
            onSave(url.hostname);
            setIsEditing(false);
        } catch (e) {
            toast.error("Invalid domain format. Please use a format like 'example.com'");
        }
    };

    if (!isEditing && websiteConfig?.domain) {
        return (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center text-[#1E6C56]">
                        <Globe className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-500 mb-1">Registered Website</p>
                        <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            {websiteConfig.domain}
                            <span className="bg-green-100 text-[#1E6C56] text-xs px-2 py-0.5 rounded font-semibold">Active</span>
                        </h3>
                    </div>
                </div>
                <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors font-medium text-sm"
                >
                    <Edit2 className="w-4 h-4" />
                    Change Domain
                </button>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl border border-[#E0E4E2] shadow-sm overflow-hidden">
            <div className="p-6 border-b border-[#E0E4E2] bg-[#FDFDFD]">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-[#1E6C56]" />
                    {websiteConfig?.domain ? 'Change Registered Website' : 'Register Your Website'}
                </h3>
                <p className="text-gray-500 text-sm mt-1">Enter the domain where you will install the translation widget.</p>
            </div>

            <div className="p-6 space-y-4">
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Website Domain</label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <LinkIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            value={domain}
                            onChange={(e) => setDomain(e.target.value)}
                            placeholder="e.g., yourstore.com"
                            className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-[#FFB800] focus:border-[#FFB800] text-gray-900 font-medium"
                        />
                    </div>
                    <p className="mt-2 text-xs text-gray-500">Do not include http:// or pathnames. Just the root domain.</p>
                </div>

                <div className="flex gap-3 pt-2">
                    <button
                        onClick={handleSave}
                        disabled={isProcessing || !domain.trim()}
                        className="flex-1 bg-[#1E6C56] hover:bg-[#175443] text-white py-3 px-4 rounded-lg font-medium flex justify-center items-center transition-colors disabled:opacity-50"
                    >
                        {isProcessing ? (
                            <span className="flex items-center gap-2">
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                Saving...
                            </span>
                        ) : (
                            <span className="flex items-center gap-2">
                                <Check className="w-4 h-4" />
                                Save Domain
                            </span>
                        )}
                    </button>
                    {websiteConfig?.domain && (
                        <button
                            onClick={() => {
                                setDomain(websiteConfig.domain);
                                setIsEditing(false);
                            }}
                            className="px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium flex items-center justify-center gap-2"
                        >
                            <X className="w-4 h-4" />
                            Cancel
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WebsiteManager;
